<template>
    <page-title
        title="アプリ申し込み"
        icon="bi-clipboard-plus"
        :backTo="{ name: 'PageSchoolList' }"
    >
    </page-title>

    <inline-loader v-if="loading" />

    <div v-else>
        <summary-box title="学校">
            <summary-box-row :text="this.school.school_name"/>
            <summary-box-row :text="'（' + this.school.full_address + '）'"/>
        </summary-box>

        <form class="mt-3" @submit.prevent="$refs.confirm_save.show();">
            <div class="row mb-4">
                <div class="col-8 col-md-6">
                    <label class="form-label">年度</label>
                    <form-select
                        v-model="order.year"
                        :options="option_years"
                        :required=true
                        @change="getAppList"
                    ></form-select>
                </div>
                <div class="col-10 col-md-6">
                    <label class="form-label">アプリ種類</label>
                    <form-select
                        v-model="order.app_key"
                        :options="apps"
                        option_key="app_key"
                        option_label="app_name"
                        :empty_option="app_list_loading ? '読込中...' : '--選択--'"
                        :required=true
                        model_type="string"
                    ></form-select>
                </div>
            </div>

            <hr class="mb-3">

            <div class="row mt-md-3" v-for="(detail, index) in order.details" :key="index">
                <div class="col-8 col-md-4">
                    <label class="form-label">学年</label>
                    <form-select
                        v-model="detail.grade"
                        :options="grades"
                        empty_option="--選択--"
                        :required="index === 0"
                    ></form-select>
                </div>
                <div class="col-16 col-md-8 row">
                    <label class="form-label">{{ displayClassType(index) }}</label>
                    <div class="col input-group">
                        <select
                            v-model="detail.school_class.school_class_id"
                            class="form-control"
                            :required="index === 0"
                        >
                            <option value="" selected>--選択--</option>
                            <template
                                v-if="order.year &&
                                detail.grade &&
                                (order.year in school_classes) &&
                                (detail.grade in school_classes[order.year])"
                            >
                                <template
                                    v-for="school_class in school_classes[order.year][detail.grade]"
                                    :key="school_class.school_class_id"
                                >
                                    <option :value="school_class.school_class_id">
                                        {{ school_class.class_name_display }}
                                    </option>
                                </template>
                            </template>
                        </select>
                        <!-- クラス追加モーダルOPENボタン -->
                        <button-dom-add
                            class="disabled_btn"
                            color="btn-outline-secondary"
                            :disabled="$helper.isNud(detail.grade)"
                            text=""
                            icon="bi-plus"
                            @click="openModalClassAdd(index)"
                        ></button-dom-add>
                    </div>
                </div>
                <div class="col-10 col-md-5 mt-2 mt-md-0">
                    <label class="form-label">人数</label>
                    <div class="input-group">
                        <input
                            v-model="detail.account_number"
                            type="number"
                            class="form-control"
                            step="1"
                            min="0"
                            :required="index === 0"
                        >
                        <span class="input-group-text">人</span>
                    </div>
                </div>
                <!-- 削除ボタン -->
                <div class="col col-md-6 align-self-end text-end text-md-start">
                    <button-dom-remove text="行削除" @click="removeRow(index)"></button-dom-remove>
                </div>

                <hr class="d-block d-md-none mt-3">
            </div>

            <!-- 行追加ボタン -->
            <div class="d-flex justify-content-center justify-content-md-end">
                <button-dom-add @click="addRow()"></button-dom-add>
            </div>

            <hr>

            <text-info class="mt-3">
                先生アカウントは初回申し込みの場合のみ学年数分発行されます。<br>
                ２回目以降の申込みで、追加の先生アカウントが必要な場合や、
                １学年で複数アカウント必要な場合は<br>
                備考欄にその旨をご記入ください。
            </text-info>

            <label class="form-label mt-3">担当先生</label>
            <div class="col-14 col-md-8">
                <div class="input-group">
                    <input type="text" placeholder="三和 一郎" v-model="order.recipient_name" class="form-control" required>
                    <span class="input-group-text">先生</span>
                </div>
            </div>

            <label class="form-label mt-3">学校連絡先電話番号</label>
            <div class="col-14 col-md-8">
                <div class="input-group">
                    <input type="text" placeholder="06-6789-3336" v-model="order.recipient_phone" class="form-control" pattern="^(0{1}\d{1,4}-{0,1}\d{1,4}-{0,1}\d{4})$" required>
                </div>
            </div>

            <label class="form-label mt-3">発送希望日 <span class="badge bg-secondary">任意</span></label>
            <div class="row align-items-center">
                <div class="col-12 col-md-8">
                    <input
                        type="date"
                        v-model="order.shipping_date"
                        class="form-control"
                        :min="min_shipping_date"
                    >
                </div>
            </div>
            <text-info class="mt-3">
                メール便のため、着日は指定できません。
                最短翌営業日の発送です。<br>
                希望日が休業日の場合、翌営業日の発送となります。
            </text-info>

            <label class="form-label mt-3">備考</label>
            <textarea v-model="order.note" class="form-control"></textarea>

            <hr class="mt-4">

            <!-- 合計欄 -->
            <conclusion-row
                title="合計"
                :text="$helper.number(account_number_sum)"
                unit="ライセンス"
            ></conclusion-row>
            <conclusion-row
                title="卸価"
                :text="$helper.number(unit_price)"
                unit="円／人"
            ></conclusion-row>
            <conclusion-row
                title="合計金額"
                :text="$helper.number(total_price)"
                unit="円"
            ></conclusion-row>
            <conclusion-row
                title="送料"
                :text="$helper.number(shipping_fee)"
                unit="円"
            ></conclusion-row>
            <text-info class="mt-3">同一学校の複数学年は、１回でまとめて注文してください。複数回で注文された場合、追加の送料をいただく場合があります。</text-info>

            <!-- 申込ボタン -->
            <div class="my-5">
                <button-wrap>
                    <template v-slot:default>
                        <button-exec-edit
                            text="申し込み"
                        ></button-exec-edit>
                    </template>
                </button-wrap>
            </div>
        </form>
    </div>

<!-- クラス追加モーダル -->
<div ref="modal_add_class" v-if="modal_shown">
    <transition name="fade">
        <div v-if="modal_shown">
            <div class="modal-backdrop show"></div>
            <div class="modal" tabindex="-1" style="display:block">
                <div class="modal-dialog">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h4 class="modal-title">{{ displayClassType(modal_index) }}の追加</h4>
                            <button type="button" class="btn-close" @click="closeModalClassAdd"></button>
                        </div>
                        <div class="modal-body">
                            <summary-box title="対象">
                                <summary-box-row
                                    :text="this.school.school_name"
                                />
                                <summary-box-row
                                    :text="order.details[modal_index].grade ? Grade.getWhereSchoolType(this.school.school_type, order.details[modal_index].grade) : '選択なし'"
                                />
                            </summary-box>
                            <template
                                v-if="$helper.isNud(order.details[modal_index].grade) ||
                                $helper.isNud(order.year)"
                            >
                                <text-warn class=" mt-4">
                                    {{ displayClassType(modal_index) }}を追加できません。このウインドウを閉じ、先に学年を指定してください。
                                </text-warn>
                            </template>
                            <template v-else>
                                <div class="row justify-content-center">
                                    <text-info class="col-24 text-center mt-4">
                                        追加する{{ displayClassType(modal_index) }}名を入力して追加ボタンをクリックしてください。
                                    </text-info>
                                    <!-- クラス追加フォーム -->
                                    <form @submit.prevent="submitAddClass(modal_index)">
                                        <div class="row align-items-center mb-3 mx-0 mx-md-5">
                                            <div class="col-24 col-md-12 col-lg-10 me-5">
                                                <div class="input-group">
                                                    <input
                                                        type="text"
                                                        v-model="new_class_name"
                                                        class="form-control"
                                                        required
                                                        :disabled="Number(new_class_is_singular)"
                                                    >
                                                    <span class="input-group-text" v-if="!is_club">組</span>
                                                </div>
                                            </div>
                                            <form-checkbox
                                                v-if="!is_club"
                                                v-model="new_class_is_singular"
                                                option_label="クラス名なし（単学級）"
                                                @change="changeClassNameValue()"
                                                class="col"
                                            ></form-checkbox>
                                        </div>
                                        <div class="text-center mt-4">
                                            <button-exec-edit text="追加" />
                                        </div>
                                    </form>
                                </div>
                            </template>
                        </div>
                        <div class="modal-footer">
                            <button-wrap>
                                <template v-slot:negative>
                                    <button-dom-close @click="closeModalClassAdd"></button-dom-close>
                                </template>
                            </button-wrap>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>
</div>

<confirm-dialog ref="confirm_save" @ok="save">
    <p>申込みを確定してよろしいですか</p>
</confirm-dialog>

</template>

<script>
import SubmitOrder from '@/shared/models/services/submit-order';
import Grade from '@/shared/models/enums/grade';
import Order from '@/shared/models/entities/order';
import OrderDetail from '@/shared/models/entities/order-detail';
import School from '@/shared/models/entities/school';
import SchoolClass from '@/shared/models/entities/school-class';
import FormCheckbox from '@/shared/components/form/FormCheckbox';
import FormSelect from '@/shared/components/form/FormSelect';
import PageTitle from '@/shared/components/design/PageTitle';
import ConfirmDialog from '@/shared/components/tool/ConfirmDialog';
import InlineLoader from '@/shared/components/tool/InlineLoader';
import SummaryBox from '@/shared/components/design/SummaryBox';
import SummaryBoxRow from '@/shared/components/design/SummaryBoxRow';
import ConclusionRow from '@/shared/components/design/ConclusionRow';
import TextInfo from '@/shared/components/design/TextInfo';
import TextWarn from '@/shared/components/design/TextWarn';
import ButtonDomAdd from '@/shared/components/design/buttons/ButtonDomAdd';
import ButtonDomRemove from '@/shared/components/design/buttons/ButtonDomRemove';
import ButtonExecEdit from '@/shared/components/design/buttons/ButtonExecEdit';
import ButtonDomClose from '@/shared/components/design/buttons/ButtonDomClose';
import ButtonWrap from '@/shared/components/design/buttons/ButtonWrap';


export default {
    name: 'PageSchoolOrder',
    inject: [
        'startScreenLoading',
        'endScreenLoading',
        'showMessage',
        'showErrorMessage',
    ],
    components: {
        InlineLoader,
        ConfirmDialog,
        FormCheckbox,
        FormSelect,
        PageTitle,
        SummaryBox,
        SummaryBoxRow,
        ConclusionRow,
        TextInfo,
        TextWarn,
        ButtonDomAdd,
        ButtonDomRemove,
        ButtonExecEdit,
        ButtonDomClose,
        ButtonWrap,
    },
    data() {
        return {
            loading: 0,
            app_list_loading: false,

            //表示系
            school: new School(),

            //選択肢
            apps: [],
            years: SubmitOrder.orderableYears(),
            school_classes: {},

            //送信データ
            order: new Order(),

            //モーダル
            modal_shown: false,
            new_class_name: '',
            new_class_is_singular: '0',
            modal_index: null,
            Grade: Grade,
        }
    },
    mounted() {
        //空のdetailを３つ代入（初期は３行表示しておく）
        this.pushThreeOrderDetailsForInit();

        //orderに年度を設定
        this.order.year = SubmitOrder.orderableYears()[0];

        //アプリの選択肢を設定
        this.getAppList();

        //学校情報を取得
        this.fetchSchool();

        // クラス情報を取得
        this.fetchSchoolClasses();
    },
    computed: {
        is_club() {
            if (
                this.order.details[this.modal_index].grade === Grade.ESC
                || this.order.details[this.modal_index].grade === Grade.JHC
            ) {
                return true;
            }

            return false
        },
        //発送希望日を翌日より選べるようにする
        min_shipping_date() {
            //今日の日付
            let today = new Date();
            //翌日の日付
            let tomorrow = today.setDate(today.getDate() + 1);

            //yyyy-mm-ddの形にして返す
            return this.$helper.ymdForInputDate(tomorrow);
        },
        grades() {
            return Grade.optionsWhereSchoolType(this.school.school_type);
        },
        //児童の人数を合計を表示
        account_number_sum() {
            let total = 0;
            for (let detail of this.order.details) {
                if (this.$helper.isNud(detail.account_number)) {
                    continue;
                }
                total += parseInt(detail.account_number);
            }
            return total;
        },
        //選ばれたアプリの単価を表示
        unit_price() {
            if (this.order.app_key === null) {
                return 0;
            }
            let selected_app = this.apps.find((app) => {
                return app.app_key === this.order.app_key;
            });
            if (typeof selected_app === 'undefined') {
                return 0;
            }
            return selected_app.unit_price;
        },
        //申込みの合計金額を表示
        total_price() {
            if (this.unit_price === null) {
                return 0;
            }
            return this.account_number_sum * this.unit_price;
        },
        //選ばれたアプリの送料を表示
        shipping_fee() {
            if (this.order.app_key === null) {
                return 0;
            }
            let selected_app = this.apps.find((app) => {
                return app.app_key === this.order.app_key;
            });
            if (typeof selected_app === 'undefined') {
                return 0;
            }
            return selected_app.shipping_fee;
        },
        //年度の配列をオブジェクトの配列に変換
        option_years() {
            return this.years.map(year => ({key: year, label: year + '年度'}));
        }
    },
    methods: {
        //空のdetailを３つ代入（初期は３行表示しておく）
        pushThreeOrderDetailsForInit() {
            for (let i = 1; i <= 3; i++) {
                this.order.details.push(new OrderDetail());
            }
        },
        //アプリリストの取得
        getAppList() {
            this.apps = [];

            if (this.order.year === null) {
                return;
            }

            //select用アプリ一覧を取得
            this.app_list_loading = true;
            this.$http.get('/distributor/app', {
                params: {year: this.order.year}
            })
            .then(response => {
                this.apps = response.data;
            }).finally(() => {
                this.app_list_loading = false;
            });
        },
        // 学校情報を取得
        fetchSchool() {
            this.loading++;

            this.$http.get('/distributor/school/' + parseInt(this.$route.params.school_id, 10))
            .then(response => {
                this.school = new School(response.data);
                this.order.school = this.school;
            })
            .finally(() => {
                this.loading--;
            });
        },
        //クラス情報を取得
        fetchSchoolClasses() {
            for (let year of this.years) {
                this.loading++;

                this.school_classes[year] = {};
                this.$http.get(`/distributor/school/${parseInt(this.$route.params.school_id, 10)}/year/${year}`)
                .then(response => {
                    let grades = response.data.classes;

                    for (let grade in grades) {
                        this.school_classes[year][grade] = [];

                        for (let school_class of grades[grade]) {
                            this.school_classes[year][grade].push(new SchoolClass(school_class));
                        }
                    }
                })
                .finally(() => {
                    this.loading--;
                });
            }
        },
        // クラス or クラブ活動 or 部活動
        displayClassType(index) {
            if (this.order.details[index].grade === Grade.ESC ) {
                return 'クラブ活動';
            }

            if (this.order.details[index].grade === Grade.JHC) {
                return '部活動';
            }

            return 'クラス';
        },
        //申込送信
        save() {
            this.startScreenLoading();

            // 入力されていないdetailは省く
            this.removeEmptyOrderDetails();

            this.$http.post('/distributor/order', this.order)
            .then(response => {
                //リダイレクトする
                this.showMessage('登録しました');
                this.$router.push('/distributor/order/' + response.data.order_id);
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },
        // 学年選択の空行を取り除いて詰め直す
        removeEmptyOrderDetails() {
            let filled_details = this.order.details.filter(detail => {
                return (
                    detail.grade
                    && detail.school_class.school_class_id
                    && detail.account_number > 0
                );
            });
            this.order.details.splice(0);
            this.order.details.push(...filled_details);
        },
        //クラス追加
        submitAddClass(index) {
            this.startScreenLoading();
            this.$http.post(`/distributor/school/${parseInt(this.$route.params.school_id, 10)}/class`, {
                year: this.order.year,
                grade: this.order.details[this.modal_index].grade,
                class_name: this.new_class_name,
                is_singular: this.new_class_is_singular,
            })
            .then(response => {
                this.closeModalClassAdd();
                //クラス情報に追加
                let new_school_class = new SchoolClass(response.data);

                if (
                    !Array.isArray(this.school_classes[new_school_class.year][new_school_class.grade])
                ) {
                    this.school_classes[new_school_class.year][new_school_class.grade] = [];
                }
                this.school_classes[new_school_class.year][new_school_class.grade].push(new_school_class);

                this.order.details[index].school_class.school_class_id = new_school_class.school_class_id;
                this.showMessage('クラスを追加しました');
            })
            .finally(() => {
                this.endScreenLoading();
            });
        },
        //単学級選択時
        changeClassNameValue() {
            if (Number(this.new_class_is_singular)) {
                this.new_class_name = '';
            }
        },
        //行追加
        addRow() {
            this.order.details.push(new OrderDetail());
        },
        //行削除
        removeRow(index) {
            this.order.details.splice(index, 1);
        },
        openModalClassAdd(index) {
            this.modal_index = index; //クラス作成後に注文のクラスIDをセットするため
            this.new_class_name = '';
            this.new_class_is_singular = '0';
            this.modal_shown = true;
        },
        closeModalClassAdd() {
            this.modal_shown = false;
        },
    },

}
</script>

<style scoped>
    @media (min-width: 900px) {
        .modal-dialog { max-width: 800px; margin: 1.75rem auto; }
    }
</style>
